import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Clock,
  ExternalLink,
  Eye,
  Loader2,
  PenLine,
  Paperclip,
  Ticket,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Main } from "@/components/Main";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { approveEndorsement, getEndorsements } from "@/Api/endorsements";
import {
  Endorsement,
  ENDORSEMENT_STATUS_LABEL,
  EndorsementStatus,
  EndorsementTotals,
} from "@/types/endorsement";
import { PERMISSIONS } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { Mask } from "@/utils/mask";
import { appDialogSecondaryButton } from "@/components/ui/app-dialog";
import { SalesFilters, SalesFiltersComponent } from "@/components/SalesFilters";
import { getSelectSellers } from "@/Api/sellers";
import { getSelectProductGroups } from "@/Api/productGroups";
import { getSelectContests } from "@/Api/contests";
import { EndorsementAttachmentsModal } from "@/App/Endorsements/AttachmentsModal";
import { EndorsementRejectModal } from "@/App/Endorsements/RejectModal";
import { EndorsementDetailsModal } from "@/App/Endorsements/DetailsModal";

const STATUS_STYLE: Record<EndorsementStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const STATUS_ICON: Record<EndorsementStatus, React.ReactNode> = {
  pending: <Clock className="h-3 w-3" />,
  approved: <CheckCircle2 className="h-3 w-3" />,
  rejected: <XCircle className="h-3 w-3" />,
};

const APPROVE_BUTTON =
  "border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300";
const REJECT_BUTTON =
  "border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300";

const EMPTY_TOTALS: EndorsementTotals = {
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
  tickets_quantity: 0,
  total_value: 0,
};

type Option = { id: number; name: string };

const PER_PAGE_DEFAULT = 20;

// Data local em vez de toISOString(), que converte para UTC
const todayLocal = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

/** Mesmo padrão da lista de vendas: período "Hoje" e 20 registros por página. */
const defaultFilters = (): SalesFilters => ({
  start_date: todayLocal(),
  end_date: todayLocal(),
  date_period: "today",
  per_page: PER_PAGE_DEFAULT,
});

type Pagination = { current_page: number; last_page: number; total: number };

/**
 * Vendas > Endossamento (LOT-34).
 * Vendedor vê só as solicitações dele; quem tem a permissão de revisão vê todas e aprova/recusa.
 */
export const PageList = () => {
  const { hasPermission, isAdmin } = usePermissions();
  const canReview = isAdmin() || hasPermission(PERMISSIONS.REVIEW_ENDORSEMENT_REQUESTS);

  // "filters" é o que está sendo editado; "appliedFilters" é o que a lista está usando (só muda ao aplicar)
  const [filters, setFilters] = useState<SalesFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<SalesFilters>(filters);
  const [status, setStatus] = useState("all");
  const [appliedStatus, setAppliedStatus] = useState("all");
  const [sellers, setSellers] = useState<Array<{ id: number; user: { name: string } }>>([]);
  const [lotteries, setLotteries] = useState<Option[]>([]);
  const [contests, setContests] = useState<Option[]>([]);
  const [rows, setRows] = useState<Endorsement[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ current_page: 1, last_page: 1, total: 0 });
  const [totals, setTotals] = useState<EndorsementTotals>(EMPTY_TOTALS);
  const [loading, setLoading] = useState(true);
  const [attachmentsOf, setAttachmentsOf] = useState<Endorsement | null>(null);
  const [detailsOf, setDetailsOf] = useState<Endorsement | null>(null);
  const [rejecting, setRejecting] = useState<Endorsement | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);

  const fetchData = useCallback(async (f: SalesFilters, st: string, page = 1, silent = false) => {
    if (f.date_period === "custom" && f.start_date && f.end_date && f.start_date > f.end_date) {
      toast.error("A data final deve ser igual ou posterior à data inicial.");
      return;
    }

    if (!silent) setLoading(true);
    try {
      const { ok, data } = await getEndorsements(page, {
        status: st === "all" ? undefined : st,
        sale_id: f.sale_id?.trim(),
        client_name: f.client_name?.trim(),
        seller_id: f.seller_id,
        lottery_id: f.lottery_id,
        contest_id: f.contest_id,
        // Com ID da venda, o período não restringe (igual à lista de vendas)
        start_date: f.sale_id?.trim() ? undefined : f.start_date,
        end_date: f.sale_id?.trim() ? undefined : f.end_date,
        per_page: Number(f.per_page) || PER_PAGE_DEFAULT,
      });
      if (ok && data?.response) {
        setRows(data.response.data);
        setTotals(data.response.totals ?? EMPTY_TOTALS);
        setPagination({
          current_page: data.response.current_page,
          last_page: data.response.last_page,
          total: data.response.total,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(appliedFilters, appliedStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  useEffect(() => {
    getSelectProductGroups().then(({ ok, data }) => ok && setLotteries(data?.response || []));
    getSelectContests().then(({ ok, data }) => ok && setContests(data?.response || []));
  }, []);

  useEffect(() => {
    if (!canReview) return;
    getSelectSellers({ is_active: true }).then(({ data }) => setSellers(data?.response || []));
  }, [canReview]);

  const handleApply = () => {
    setAppliedFilters(filters);
    setAppliedStatus(status);
    fetchData(filters, status);
  };

  const handleClear = () => {
    const defaults = defaultFilters();
    setFilters(defaults);
    setAppliedFilters(defaults);
    setStatus("all");
    setAppliedStatus("all");
    fetchData(defaults, "all");
  };

  const handleApprove = async (item: Endorsement) => {
    // LOT-68: aprovação exige ao menos um anexo (o back também valida)
    if (item.files.length === 0) {
      toast.error("Anexe ao menos uma foto ou vídeo antes de aprovar o endossamento.");
      return;
    }

    setApprovingId(item.id);
    try {
      const { ok } = await approveEndorsement(item.uuid);
      if (!ok) return;

      toast.success("Endossamento aprovado.");
      fetchData(appliedFilters, appliedStatus, pagination.current_page, true);
    } finally {
      setApprovingId(null);
    }
  };

  const renderActions = (item: Endorsement) => (
          <div className="flex flex-wrap items-center justify-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className={`h-8 gap-1 px-2 ${appDialogSecondaryButton}`}
              title="Anexar / ver foto e vídeo"
              onClick={() => setAttachmentsOf(item)}
            >
              <Paperclip className="h-4 w-4" />
              {item.files.length}
            </Button>
            <Button
              variant="outline"
              size="icon"
              className={`h-8 w-8 ${appDialogSecondaryButton}`}
              title="Ver detalhes"
              onClick={() => setDetailsOf(item)}
            >
              <Eye className="h-4 w-4 text-primary" />
            </Button>
            {canReview && item.status === "pending" && (
              <>
                <span
                  className="inline-flex"
                  title={
                    item.files.length === 0
                      ? "Anexe ao menos uma foto ou vídeo para aprovar"
                      : undefined
                  }
                >
                  <Button
                    variant="outline"
                    size="icon"
                    className={`h-8 w-8 ${APPROVE_BUTTON}`}
                    title="Aprovar"
                    disabled={approvingId === item.id || item.files.length === 0}
                    onClick={() => handleApprove(item)}
                  >
                    {approvingId === item.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                  </Button>
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className={`h-8 w-8 ${REJECT_BUTTON}`}
                  title="Recusar"
                  onClick={() => setRejecting(item)}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
  );

  return (
    <Main>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
          AP
        </div>
        <div>
          <h2 className="text-2xl font-semibold leading-tight">Endosso Bilhetes</h2>
          <p className="text-sm text-muted-foreground">Vendas / Endossamento</p>
        </div>
      </div>

      <SalesFiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
        onApplyFilters={handleApply}
        onClearFilters={handleClear}
        sellers={canReview ? sellers : []}
        lotteries={lotteries}
        contests={contests}
        hideSaleStatus
        hideSaleSpecificFilters
        extraFilters={
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {(Object.keys(ENDORSEMENT_STATUS_LABEL) as EndorsementStatus[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {ENDORSEMENT_STATUS_LABEL[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted-foreground">Solicitações</p>
              <p className="text-2xl font-bold tabular-nums">{totals.total}</p>
              <p className="text-xs text-muted-foreground">
                {totals.pending} pendentes · {totals.approved} aprovadas · {totals.rejected} recusadas
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30">
              <PenLine className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted-foreground">Qtd. de bilhetes</p>
              <p className="text-2xl font-bold tabular-nums">{totals.tickets_quantity}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900/30">
              <Ticket className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted-foreground">Valor total</p>
              <p className="text-2xl font-bold tabular-nums text-green-700 dark:text-green-400">
                {Mask.formatCurrency(totals.total_value)}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30">
              <DollarSign className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mobile: um card por solicitação (a tabela larga fica só do lg pra cima) */}
      <div className="space-y-3 lg:hidden">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Nenhuma solicitação de endossamento encontrada.
          </p>
        ) : (
          rows.map((item) => {
            const date = new Date(item.created_at);
            const lotteries = item.products.reduce<{ name: string; color: string | null }[]>((acc, p) => {
              if (p.lottery && !acc.some((l) => l.name === p.lottery)) acc.push({ name: p.lottery, color: p.lottery_color });
              return acc;
            }, []);

            return (
              <Card key={item.uuid}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{item.client_name}</p>
                      <Link
                        to={`/vendas?sale_id=${item.sale_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                      >
                        #{item.sale_id}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                      {canReview && item.seller_name && (
                        <p className="text-xs text-muted-foreground">Vend.: {item.seller_name}</p>
                      )}
                    </div>
                    <span
                      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[item.status]}`}
                    >
                      {STATUS_ICON[item.status]}
                      {ENDORSEMENT_STATUS_LABEL[item.status]}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {lotteries.map((lottery) => (
                      <span
                        key={lottery.name}
                        className="inline-flex rounded px-2 py-0.5 text-xs font-bold uppercase text-white"
                        style={{ backgroundColor: lottery.color || "#475569" }}
                      >
                        {lottery.name}
                      </span>
                    ))}
                    {[...new Set(item.products.map((p) => p.contest).filter(Boolean))].map((contest) => (
                      <span key={contest} className="text-xs text-muted-foreground">
                        Concurso {contest}
                      </span>
                    ))}
                  </div>

                  {item.status === "rejected" && item.reviewer_notes && (
                    <p className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                      {item.reviewer_notes}
                    </p>
                  )}

                  <div className="grid grid-cols-3 gap-2 border-t pt-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Bilhetes</p>
                      <p className="font-semibold tabular-nums">{item.tickets_quantity}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Valor</p>
                      <p className="font-bold tabular-nums text-green-700 dark:text-green-400">
                        {Mask.formatCurrency(item.total_value)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Data/Hora</p>
                      <p className="text-xs">
                        {date.toLocaleDateString("pt-BR")}{" "}
                        {date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end border-t pt-3">{renderActions(item)}</div>
                </CardContent>
              </Card>
            );
          })
        )}

        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between rounded-lg bg-primary/5 px-4 py-3 text-sm">
            <span className="font-semibold">
              {totals.total} {totals.total === 1 ? "solicitação" : "solicitações"} · {totals.tickets_quantity} bilhetes
            </span>
            <span className="font-bold tabular-nums text-green-700 dark:text-green-400">
              {Mask.formatCurrency(totals.total_value)}
            </span>
          </div>
        )}
      </div>

      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome do cliente</TableHead>
                <TableHead>Produtos</TableHead>
                <TableHead>Concurso</TableHead>
                <TableHead>Loteria</TableHead>
                <TableHead className="text-center">Qtd. bilhetes</TableHead>
                <TableHead className="text-right">Valor produto</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Data/Hora</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-muted-foreground">
                    Nenhuma solicitação de endossamento encontrada.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((item) => {
                  const contests = [...new Set(item.products.map((p) => p.contest).filter(Boolean))];
                  // Uma chip por loteria, na cor cadastrada
                  const lotteries = item.products.reduce<{ name: string; color: string | null }[]>((acc, p) => {
                    if (p.lottery && !acc.some((l) => l.name === p.lottery)) {
                      acc.push({ name: p.lottery, color: p.lottery_color });
                    }
                    return acc;
                  }, []);
                  const shown = item.products.slice(0, 3).map((p) => p.name);
                  const extra = item.products.length - shown.length;
                  const date = new Date(item.created_at);

                  return (
                    <TableRow key={item.uuid}>
                      <TableCell>
                        <p className="font-semibold">{item.client_name}</p>
                        <Link
                          to={`/vendas?sale_id=${item.sale_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-blue-600 hover:underline"
                          title="Abrir a venda em nova aba"
                        >
                          #{item.sale_id}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                        {canReview && item.seller_name && (
                          <p className="text-xs text-muted-foreground">Vend.: {item.seller_name}</p>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] text-sm">
                        {shown.length > 0
                          ? `${shown.join(", ")}${extra > 0 ? ` + ${extra} ${extra === 1 ? "produto" : "produtos"}` : ""}`
                          : "—"}
                      </TableCell>
                      <TableCell>{contests.join(", ") || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {lotteries.map((lottery) => (
                            <span
                              key={lottery.name}
                              className="inline-flex whitespace-nowrap rounded px-2 py-0.5 text-xs font-bold uppercase text-white"
                              style={{ backgroundColor: lottery.color || "#475569" }}
                            >
                              {lottery.name}
                            </span>
                          ))}
                          {lotteries.length === 0 && "—"}
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-lg font-semibold tabular-nums">
                        {item.tickets_quantity}
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums text-green-700 dark:text-green-400">
                        {Mask.formatCurrency(item.total_value)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[item.status]}`}
                          title={item.reviewer_notes ?? undefined}
                        >
                          {STATUS_ICON[item.status]}
                          {ENDORSEMENT_STATUS_LABEL[item.status]}
                        </span>
                        {item.status === "rejected" && item.reviewer_notes && (
                          <p className="mt-1 max-w-[180px] text-xs text-muted-foreground">{item.reviewer_notes}</p>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <p className="text-sm">{date.toLocaleDateString("pt-BR")}</p>
                        <p className="text-xs text-muted-foreground">
                          {date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </TableCell>
                      <TableCell>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            <TableFooter>
              <TableRow className="bg-primary/5 hover:bg-primary/5">
                <TableCell colSpan={4} className="py-3 text-sm font-semibold">
                  Total do filtro ({totals.total} {totals.total === 1 ? "solicitação" : "solicitações"})
                </TableCell>
                <TableCell className="py-3 text-center font-bold tabular-nums">{totals.tickets_quantity}</TableCell>
                <TableCell className="py-3 text-right text-base font-bold tabular-nums text-green-700 dark:text-green-400">
                  {Mask.formatCurrency(totals.total_value)}
                </TableCell>
                <TableCell colSpan={3} className="py-3 text-xs text-muted-foreground">
                  {totals.pending} pendentes · {totals.approved} aprovadas · {totals.rejected} recusadas
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      {pagination.last_page > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium">
            Página {pagination.current_page} de {pagination.last_page}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className={appDialogSecondaryButton}
              disabled={loading || pagination.current_page <= 1}
              onClick={() => fetchData(appliedFilters, appliedStatus, pagination.current_page - 1)}
            >
              <ChevronLeft className="h-4 w-4" /> Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={appDialogSecondaryButton}
              disabled={loading || pagination.current_page >= pagination.last_page}
              onClick={() => fetchData(appliedFilters, appliedStatus, pagination.current_page + 1)}
            >
              Próxima <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <EndorsementAttachmentsModal
        endorsement={attachmentsOf}
        onClose={() => setAttachmentsOf(null)}
        onChanged={() => fetchData(appliedFilters, appliedStatus, pagination.current_page, true)}
      />
      <EndorsementDetailsModal endorsement={detailsOf} onClose={() => setDetailsOf(null)} />
      <EndorsementRejectModal
        endorsement={rejecting}
        onClose={() => setRejecting(null)}
        onSuccess={() => {
          setRejecting(null);
          fetchData(appliedFilters, appliedStatus, pagination.current_page, true);
        }}
      />
    </Main>
  );
};
